import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { PrivateLayoutContext } from "../components/layout-mui/PrivateLayout";
import { useDashboard } from "../components/dashboard-engine/hooks/useDashboard";
import { VariableRenderer } from "../components/dashboard-engine/variables/VariableRenderer";
import { PanelRenderer } from "../components/dashboard-engine/panels/PanelRenderer";
import { useDashboardLayout } from "../components/dashboard-engine/hooks/useDashboardLayout";
import { DashboardViewSettings } from "../components/dashboard-engine/layout/DashboardViewSettings";

const ResponsiveGridLayout = WidthProvider(Responsive);

export const ControlGestion = () => {
  const { setHeaderContent } = useContext(PrivateLayoutContext);
  const { dashboardId } = useParams();
  const dashboard = useDashboard(dashboardId);
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    setHeaderContent("Control de gestión");
  }, [setHeaderContent]);

  useEffect(() => {
    const firstTab = dashboard.layout?.tabs?.[0]?.id;

    if (!activeTab && firstTab) {
      setActiveTab(firstTab);
    }
  }, [activeTab, dashboard.layout]);

  useEffect(() => { setActiveTab(""); }, [dashboardId]);

  const tab = useMemo(
    () =>
      dashboard.layout?.tabs?.find((item) => item.id === activeTab) ||
      dashboard.layout?.tabs?.[0] ||
      null,
    [activeTab, dashboard.layout]
  );

  const dashboardLayout = useDashboardLayout({
    source: dashboard.source,
    tab,
  });

  const gridItems = dashboardLayout.layouts[dashboardLayout.breakpoint] || [];

  const visibleVariables = dashboard.variables.filter(
    (variable) => variable.nombre !== "grafana_auth" && !variable.oculto && variable.tipo !== "constant"
  );

  if (dashboard.unavailable || (!dashboard.loading && !dashboard.layout && dashboard.error)) {
    return <Box sx={{p: 4, textAlign: "center"}}><Typography variant="h6">Ups... este contenido ya no está disponible</Typography></Box>;
  }

  if (dashboard.loading) {
    return (
      <Box
        sx={{
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: 2, boxSizing: "border-box" }}>
      {dashboard.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {dashboard.error}
        </Alert>
      )}

      {visibleVariables.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
            {visibleVariables.map((variable) => (
              <VariableRenderer
                key={variable.nombre}
                definition={variable}
                value={dashboard.values[variable.nombre]}
                options={dashboard.options[variable.nombre] || []}
                loading={!!dashboard.loadingVariables[variable.nombre]}
                onChange={(value) => dashboard.setValue(variable.nombre, value)}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {(dashboard.layout?.tabs || []).length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            pr: 1,
            gap: 1,
          }}
        >
          <Tabs
            value={tab?.id || false}
            onChange={(event, value) => { setActiveTab(value); dashboard.checkVersion(); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {dashboard.layout.tabs.map((item) => (
              <Tab key={item.id} value={item.id} label={item.titulo} />
            ))}
          </Tabs>

          <DashboardViewSettings
            tab={tab}
            panels={dashboard.layout?.panels}
            layoutState={dashboardLayout}
          />
        </Paper>
      )}

      {tab && (
        <ResponsiveGridLayout
          layouts={dashboardLayout.layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 24, md: 24, sm: 24, xs: 24 }}
          rowHeight={42}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          isDraggable={dashboardLayout.editing}
          isResizable={dashboardLayout.editing}
          onBreakpointChange={dashboardLayout.setBreakpoint}
          onLayoutChange={(currentLayout) =>
            dashboardLayout.persistCurrentLayout(currentLayout)
          }
        >
          {gridItems.map((item) => {
            const panel = dashboard.layout?.panels?.[item.i];

            return (
              <Paper
                key={item.i}
                variant="outlined"
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  bgcolor: "background.paper",
                  outline: dashboardLayout.editing
                    ? "1px dashed"
                    : "none",
                  outlineColor: "primary.main",
                  "& iframe": {
                    pointerEvents: dashboardLayout.editing ? "none" : "auto",
                  },
                }}
              >
                <PanelRenderer panel={panel} dashboard={dashboard} />
              </Paper>
            );
          })}
        </ResponsiveGridLayout>
      )}

      {!tab && !dashboard.error && (
        <Typography color="text.secondary">
          El dashboard no contiene pestañas para mostrar.
        </Typography>
      )}
    </Box>
  );
};
